"use strict";
/* globals module */
/**
 * @module articleInstanceCommentsClient
 * @description  Article Instance Comments Client provides an easy way to consume  Article Comments REST API end-points. `articleCommentsClient` functions enable performing standard CRUD operations directly on article comment resources, whereas the `articleClient` functions allow management between article and article comments. In order to obtain needed routes `articleCommentsClient` uses `articleCommentsRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../httpApi");
var __1 = require("../");
var ArticleInstanceCommentsClient = /** @class */ (function () {
    function ArticleInstanceCommentsClient(articleInstanceCommentsRoute, articleInstanceCommentsRepliesClient, apiClient) {
        this.articleInstanceCommentsRoute = articleInstanceCommentsRoute;
        this.articleInstanceCommentsRepliesClient = articleInstanceCommentsRepliesClient;
        this.apiClient = apiClient;
        /**
       * Contains a reference to valid list of article comment states. It returns an object containing all article comment states.
       * @method
       * @example articleInstanceCommentsClient.statuses.approved;
       **/
        this.statuses = __1.CommentStatus;
    }
    Object.defineProperty(ArticleInstanceCommentsClient.prototype, "routeDefinition", {
        get: function () {
            return this.articleInstanceCommentsRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleInstanceCommentsClient.prototype, "replies", {
        get: function () {
            return this.articleInstanceCommentsRepliesClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the approve article comment action has been performed. This action sets the state of an article comment to "approved". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-approve').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @param options Notification configuration used to control the article comment recourse access when managing notification distribution.
     * @returns A promise that is resolved once the approve article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.approve(articleComment, commentOptions)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.approve = function (data, options) {
        return this.apiClient.put(this.routeDefinition.approve(data), this.routeDefinition.createParams(options));
    };
    /**
     * Returns a promise that is resolved once the unapprove article comment action has been performed. This action sets the state of an article comment to "unapproved". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-unapprove').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @returns A promise that is resolved once the unapprove article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.unapprove(articleComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.unapprove = function (data) {
        return this.apiClient.put(this.routeDefinition.unapprove(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the create article comment action has been performed; this action creates a new comment for an article.
     * @method
     * @param data An article comment object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create article comment action has been performed.
     * @example articleInstanceCommentsClient.create({
                        articleId : '<article-id>',
                        comment : <comment>,
                        userId : '<user-id>' })
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                        // perform error handling here
                    });
     **/
    ArticleInstanceCommentsClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(data), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of article comment resources matching the given criteria.
     * @method
     * @param articleId Article slug or id which uniquely identifies article whose comment resources need to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example articleInstanceCommentsClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .success(function (collection) {
                    // perform success action here
                })
                .error(function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    ArticleInstanceCommentsClient.prototype.find = function (articleId, options) {
        return this.apiClient.get(this.articleInstanceCommentsRoute.find(articleId, options));
    };
    /**
     * Returns a promise that is resolved once the flag article comment action has been performed. This action sets the state of an article comment to "flagged". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-flag').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @returns A promise that is resolved once the flag article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.flag(articleComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.flag = function (data) {
        return this.apiClient.put(this.routeDefinition.flag(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unflag article comment action has been performed. This action removes the "flagged" comment mark. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-unflag').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @returns A promise that is resolved once the unflag article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.unflag(articleComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.unflag = function (data) {
        return this.apiClient.put(this.routeDefinition.unflag(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the specified article comment resource.
     * @method
     * @param articleId Article slug or id which uniquely identifies article whose comment resource needs to be retrieved.
     * @param commentId Id which identifies article comment resource that needs to be retrieved.
     * @param options Options object that contains embed data.
     * @returns A promise that is resolved once the get action has been performed.
     * @example articleInstanceCommentsClient.get('<article-id>', '<comment-id>')
                   .then(function (data) {
                       // perform success action here
                   },
                    function (response, status, headers, config) {
                       // perform error handling here
                   });
    **/
    ArticleInstanceCommentsClient.prototype.get = function (articleId, commentId, options) {
        return this.apiClient.get(this.articleInstanceCommentsRoute.get(articleId, commentId, options));
    };
    /**
     * Returns a promise that is resolved once the remove article comment action has been performed. If the action is successfully completed, the article comment resource and its replies will be permanently removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     ```
     let params = modelMapper.removeParams(articleComment);
     let uri = params['model'].links('delete').href;
     ```
     * @method
     * @param data An article comment object used to delete specified article comment resource.
     * @returns A promise that is resolved once the remove article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                       articleInstanceCommentsClient.remove(articleComment)
                           .then(function (data) {
                               // perform success action here
                           },
                            function (response, status, headers, config) {
                               // perform error handling here
                           });
    **/
    ArticleInstanceCommentsClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the removeAll article comment action has been performed. This action will remove all comments and comment replies from an article if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleInstanceRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(articleComment);
     * let uri = params['model'].links('delete-comments-by-article').href;
     * ```
     * @method
     * @param data An article object used to delete specified article comment resource.
     * @returns A promise that is resolved once the removeAll article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.removeAll(articleComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.removeAll = function (data) {
        return this.apiClient.delete(this.routeDefinition.deleteAll(data));
    };
    /**
     * Returns a promise that is resolved once the report article comment action has been performed. This action sets the state of an article comment to "reported". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-report').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @param options Notification configuration used to control the article comment recourse access when managing notification distribution.
     * @returns A promise that is resolved once the report article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.report(articleComment, commentOptions)
                        .success(function (data) {
                            // perform success action here
                        })
                        .error(function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.report = function (data, options) {
        return this.apiClient.put(this.routeDefinition.report(data), this.routeDefinition.updateParams(options));
    };
    /**
     * Returns a promise that is resolved once the unreport article comment action has been performed. This action removes the "reported" comment mark. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-unreport').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @returns A promise that is resolved once the unreport article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.unreport(articleComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.unreport = function (data) {
        return this.apiClient.put(this.routeDefinition.unreport(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update article comment action has been performed; this action updates an article comment resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentsRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An article comments object used to update specified article comment resource.
     * @returns A promise that is resolved once the update article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceCommentsClient.update(articleComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the mark as spam article comment action has been performed. This action sets the state of an article comment to "spam". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleInstanceRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-spam').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @returns A promise that is resolved once the mark as spam article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                    articleInstanceClient.spam(articleComment)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleInstanceCommentsClient.prototype.spam = function (data) {
        return this.apiClient.put(this.routeDefinition.spam(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unspam article comment action has been performed. This action removes the "spam" comment state. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleInstanceRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleComment);
     * let uri = params['model'].links('comment-unspam').href;
     * ```
     * @method
     * @param data Article Comment object.
     * @returns A promise that is resolved once the unspam article comment action has been performed.
     * @example // articleComment is a resource previously fetched using get action.
                        articleInstanceClient.unspam(articleComment)
                            .then(function (data) {
                                // perform success action here
                            },
                             function (response, status, headers, config) {
                                 // perform error handling here
                            });
     **/
    ArticleInstanceCommentsClient.prototype.unspam = function (data) {
        return this.apiClient.put(this.routeDefinition.unspam(data), this.routeDefinition.updateParams(data));
    };
    var _a, _b, _c;
    ArticleInstanceCommentsClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(__1.TYPES.ArticleInstanceCommentsRoute)),
        tslib_1.__param(1, inversify_1.inject(__1.TYPES.ArticleCommentRepliesClient)),
        tslib_1.__param(2, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof __1.ArticleInstanceCommentsRoute !== "undefined" && __1.ArticleInstanceCommentsRoute) === "function" ? _a : Object, typeof (_b = typeof __1.ArticleInstanceCommentRepliesClient !== "undefined" && __1.ArticleInstanceCommentRepliesClient) === "function" ? _b : Object, typeof (_c = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _c : Object])
    ], ArticleInstanceCommentsClient);
    return ArticleInstanceCommentsClient;
}());
exports.ArticleInstanceCommentsClient = ArticleInstanceCommentsClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
 */ 
